import React, { useState, useEffect } from 'react';
import { crmApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Users, Plus, Search, Edit, Trash2, Phone, Mail,
  Building2, Tag, DollarSign, Loader2, ChevronRight,
  ArrowRight, Clock, Activity
} from 'lucide-react';
import LeadEmailDialog from '../../components/crm/LeadEmailDialog';

const STAGES = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];
const STAGE_LABELS = { new: 'New', contacted: 'Contacted', qualified: 'Qualified', proposal: 'Proposal', negotiation: 'Negotiation', closed_won: 'Won', closed_lost: 'Lost' };
const STAGE_COLORS = { new: '#1A56DB', contacted: '#0E7490', qualified: '#7C3AED', proposal: '#92400E', negotiation: '#DC2626', closed_won: '#166534', closed_lost: '#64748B' };
const SOURCES = ['Website', 'Referral', 'Conference', 'LinkedIn', 'Cold Call', 'Inbound Email', 'Partner'];

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [emailLead, setEmailLead] = useState(null);  // null | lead row to email
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', title: '', source: '', stage: 'new', value: '', notes: '', lead_type: 'b2b', tags: [] });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (stageFilter !== 'all') params.stage = stageFilter;
      if (typeFilter !== 'all') params.lead_type = typeFilter;
      if (search) params.search = search;
      const res = await crmApi.leads.list(params);
      setLeads(res.data.leads || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [stageFilter, typeFilter]);

  const openCreate = () => {
    setForm({ name: '', email: '', phone: '', company: '', title: '', source: '', stage: 'new', value: '', notes: '', lead_type: 'b2b', tags: [] });
    setEditId(null);
    setShowForm(true);
  };

  const openEdit = (l) => {
    setForm({ name: l.name, email: l.email || '', phone: l.phone || '', company: l.company || '', title: l.title || '', source: l.source || '', stage: l.stage, value: l.value?.toString() || '', notes: l.notes || '', lead_type: l.lead_type || 'b2b', tags: l.tags || [] });
    setEditId(l.id);
    setShowForm(true);
  };

  const openDetail = async (id) => {
    setShowDetail(id);
    try {
      const res = await crmApi.leads.get(id);
      setDetailData(res.data);
    } catch (err) { toast.error('Failed to load lead'); }
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, value: parseFloat(form.value) || 0 };
      if (editId) {
        await crmApi.leads.update(editId, payload);
        toast.success('Lead updated');
      } else {
        await crmApi.leads.create(payload);
        toast.success('Lead created');
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!showDelete) return;
    setSaving(true);
    try {
      await crmApi.leads.delete(showDelete);
      toast.success('Lead deleted');
      setShowDelete(null);
      load();
    } catch (err) { toast.error('Failed to delete'); }
    finally { setSaving(false); }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await crmApi.leads.update(leadId, { stage: newStage });
      toast.success(`Stage updated to ${STAGE_LABELS[newStage]}`);
      load();
      if (showDetail === leadId && detailData) {
        setDetailData({ ...detailData, stage: newStage });
      }
    } catch (err) { toast.error('Failed to update stage'); }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Leads</h1>
          <p className="text-[#64748B] mt-1">Manage your sales leads and patient referrals</p>
        </div>
        <Button onClick={openCreate} className="careiq-btn-primary" data-testid="create-lead-btn"><Plus className="w-4 h-4 mr-2" />New Lead</Button>
      </div>

      {/* Filters */}
      <Card className="careiq-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input placeholder="Search leads..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} className="careiq-input pl-10" data-testid="lead-search" />
              </div>
              <Button variant="outline" onClick={load}>Search</Button>
            </div>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All Stages" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {STAGES.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="b2b">B2B</SelectItem>
                <SelectItem value="patient_referral">Patient Referral</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card className="careiq-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>
          ) : leads.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="table-header text-left">Name</th>
                    <th className="table-header text-left">Company</th>
                    <th className="table-header text-left">Source</th>
                    <th className="table-header text-left">Stage</th>
                    <th className="table-header text-left">Type</th>
                    <th className="table-header text-left">Value</th>
                    <th className="table-header text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="table-row cursor-pointer hover:bg-[#F8FAFC]" data-testid={`lead-row-${l.id}`}>
                      <td className="table-cell" onClick={() => openDetail(l.id)}>
                        <div className="font-medium text-[#0A2342]">{l.name}</div>
                        {l.email && <div className="text-xs text-[#64748B]">{l.email}</div>}
                      </td>
                      <td className="table-cell text-[#64748B] text-sm">{l.company || '-'}</td>
                      <td className="table-cell text-[#64748B] text-sm">{l.source || '-'}</td>
                      <td className="table-cell">
                        <Select value={l.stage} onValueChange={(v) => handleStageChange(l.id, v)}>
                          <SelectTrigger className="h-7 text-xs border-0 p-1 w-28" style={{ backgroundColor: (STAGE_COLORS[l.stage] || '#64748B') + '15', color: STAGE_COLORS[l.stage] || '#64748B' }}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STAGES.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="table-cell text-xs text-[#64748B]">{l.lead_type === 'b2b' ? 'B2B' : l.lead_type === 'patient_referral' ? 'Referral' : 'Partner'}</td>
                      <td className="table-cell font-medium text-[#0A2342]">${(l.value || 0).toLocaleString()}</td>
                      <td className="table-cell">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEmailLead(l)}
                            disabled={!l.email}
                            title={l.email ? `Email ${l.email}` : 'No email on file'}
                            data-testid={`email-lead-${l.id}`}
                          >
                            <Mail className={`w-3.5 h-3.5 ${l.email ? 'text-[#0E7490]' : 'text-[#CBD5E1]'}`} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(l)} data-testid={`edit-lead-${l.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setShowDelete(l.id)} data-testid={`delete-lead-${l.id}`}><Trash2 className="w-3.5 h-3.5 text-[#991B1B]" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-[#64748B]"><Users className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" /><p>No leads found</p></div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Lead Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg" data-testid="lead-form-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Edit Lead' : 'New Lead'}</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-3 pr-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" /></div>
                <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="lead-company" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Deal Value ($)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="lead-value" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Stage</Label>
                  <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                    <SelectTrigger data-testid="lead-stage"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Source</Label>
                  <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.lead_type} onValueChange={(v) => setForm({ ...form, lead_type: v })}>
                    <SelectTrigger data-testid="lead-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="b2b">B2B</SelectItem>
                      <SelectItem value="patient_referral">Patient Referral</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="min-h-[80px]" data-testid="lead-notes" /></div>
            </div>
          </ScrollArea>
          <div className="flex justify-end gap-3 mt-2">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button className="careiq-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-lead-btn">{saving ? 'Saving...' : editId ? 'Update Lead' : 'Create Lead'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => { setShowDetail(null); setDetailData(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="lead-detail-dialog">
          {detailData ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between gap-2">
                  <span>{detailData.name}</span>
                  {detailData.email && (
                    <Button size="sm" variant="outline" onClick={() => { setEmailLead(detailData); setShowDetail(null); }} data-testid="detail-email-btn">
                      <Mail className="w-3.5 h-3.5 mr-1.5" /> Email
                    </Button>
                  )}
                </DialogTitle>
              </DialogHeader>
              <ScrollArea className="max-h-[60vh]">
                <div className="space-y-4 pr-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: (STAGE_COLORS[detailData.stage] || '#64748B') + '15', color: STAGE_COLORS[detailData.stage] }}>
                      {STAGE_LABELS[detailData.stage] || detailData.stage}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded">{detailData.lead_type === 'b2b' ? 'B2B' : detailData.lead_type === 'patient_referral' ? 'Referral' : 'Partner'}</span>
                    {detailData.value > 0 && <span className="text-lg font-bold text-[#0A2342]">${detailData.value.toLocaleString()}</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {detailData.email && <div className="flex items-center gap-2 text-sm text-[#64748B]"><Mail className="w-4 h-4" />{detailData.email}</div>}
                    {detailData.phone && <div className="flex items-center gap-2 text-sm text-[#64748B]"><Phone className="w-4 h-4" />{detailData.phone}</div>}
                    {detailData.company && <div className="flex items-center gap-2 text-sm text-[#64748B]"><Building2 className="w-4 h-4" />{detailData.company}</div>}
                    {detailData.source && <div className="flex items-center gap-2 text-sm text-[#64748B]"><Tag className="w-4 h-4" />{detailData.source}</div>}
                  </div>
                  {detailData.notes && <div className="p-3 bg-[#F8FAFC] rounded-lg text-sm text-[#64748B]">{detailData.notes}</div>}
                  {detailData.tags?.length > 0 && <div className="flex gap-1 flex-wrap">{detailData.tags.map((t, i) => <span key={i} className="text-[10px] px-2 py-0.5 bg-[#0E7490]/10 text-[#0E7490] rounded-full">{t}</span>)}</div>}
                  {/* Activity Timeline */}
                  <div>
                    <h3 className="text-sm font-semibold text-[#0A2342] mb-2 flex items-center gap-2"><Activity className="w-4 h-4 text-[#0E7490]" />Activity</h3>
                    <div className="space-y-2">
                      {(detailData.activities || []).slice().reverse().map((a, i) => (
                        <div key={i} className="flex gap-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-[#0E7490] mt-1.5 flex-shrink-0" />
                          <div>
                            <div className="text-[#0A2342]">{a.note}</div>
                            <div className="text-[10px] text-[#94A3B8]">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </>
          ) : (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#0E7490] animate-spin" /></div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-lead-dialog">
          <DialogHeader><DialogTitle className="text-[#991B1B]">Delete Lead</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B] mt-2">Are you sure? This action cannot be undone.</p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowDelete(null)}>Cancel</Button>
            <Button className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-lead-btn">{saving ? 'Deleting...' : 'Delete'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <LeadEmailDialog
        open={!!emailLead}
        onOpenChange={(v) => { if (!v) setEmailLead(null); }}
        lead={emailLead}
      />
    </div>
  );
}
